import { useCallback, useMemo } from "react";
import { useRouter } from "expo-router";
import * as Crypto from "expo-crypto";

import { useAuthStore } from "@src/features/auth/auth-store";
import { authenticatedFetch } from "@src/features/auth/services/authenticated-fetch";
import { getEnvironment } from "@src/config/environment-store";
import { directory } from "@src/config/runtime";
import { showInfoToast } from "@src/utils/toast";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";
import {
  createDeviceTokenApi,
  createNotificationBlacklistApi,
  usePushNotifications as usePushNotificationsBase,
  teardownDeviceToken,
} from "@visioni/a3s-notifications/app";
import type { DeviceTokenApi, NotificationBlacklistApi } from "@visioni/a3s-notifications/app";

async function directoryBaseUrl(): Promise<string> {
  const env = await getEnvironment();
  return directory(env);
}

let sharedApiClient: DeviceTokenApi | undefined;

function getOrCreatePushDeviceId(): string {
  const existing = appStorage.get<string>(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_ID);
  if (existing) return existing;
  const next = Crypto.randomUUID();
  appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_ID, next);
  return next;
}

function getDeviceTokenApi(): DeviceTokenApi {
  sharedApiClient ??= createDeviceTokenApi({
    authenticatedFetch,
    directoryBaseUrl,
    deviceId: getOrCreatePushDeviceId,
  });
  return sharedApiClient;
}

let sharedBlacklistApiClient: NotificationBlacklistApi | undefined;

function getBlacklistApi(): NotificationBlacklistApi {
  sharedBlacklistApiClient ??= createNotificationBlacklistApi({
    authenticatedFetch,
    directoryBaseUrl,
  });
  return sharedBlacklistApiClient;
}

export function usePushNotifications(options?: { enabled?: boolean }) {
  const router = useRouter();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  const deviceTokenApi = useMemo(() => getDeviceTokenApi(), []);
  const showForegroundToast = useCallback(
    (title: string, body?: string) => showInfoToast({ message: title, description: body }),
    [],
  );
  const shouldAutoRegisterToken = useCallback(
    (_token: string) => appStorage.has(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN),
    [],
  );

  usePushNotificationsBase({
    auth: { isAuthenticated, isInitialized },
    router,
    showForegroundToast,
    deviceTokenApi,
    shouldAutoRegisterToken,
    enabled: options?.enabled ?? true,
  });
}

export async function unregisterPushToken(): Promise<void> {
  appStorage.remove(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN);
  return teardownDeviceToken(getDeviceTokenApi(), { lookupToken: true });
}

export { getDeviceTokenApi, getBlacklistApi };
